"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { AddRequestOffcanvas } from "./add-request-offcanvas"
import { EditRequestOffcanvas } from "./edit"
import { RequestsList } from "./list"
import type { RequestFilters } from "./list"
import { FiX } from "react-icons/fi"
import { RequestDetailOffcanvas } from "./detail"
import api from "../../../libs/axios"
import { uploadFilesWithPresignedUrls } from "../../../libs/presigned-upload"
import { TrainingRequest, TrainingType, TrainingInstitute, Pagination } from "../types"
import type { SelectOption } from "../../../utils/select-with-search"
import { useDebounce } from "../../../hooks/useDebounce"

export const TrainingRequestsManagement: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [requests, setRequests] = useState<TrainingRequest[]>([])
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([])
  const [institutes, setInstitutes] = useState<TrainingInstitute[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0, limit: 10, page: 1, total_pages: 1, total_count: 0,
    has_next_page: false, has_prev_page: false, next_page: null, prev_page: null,
  })

  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<TrainingRequest | null>(null)
  const [duplicateRequest, setDuplicateRequest] = useState<TrainingRequest | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [associationFilter, setAssociationFilter] = useState<string>("")
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [advancedFilters, setAdvancedFilters] = useState<RequestFilters>({})
  const [designationOptions, setDesignationOptions] = useState<SelectOption[]>([])
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([])
  const [requestOptions, setRequestOptions] = useState<SelectOption[]>([])

  const fetchRequests = async (
    search = "",
    status = "",
    association = "",
    page = 1,
    limitValue?: number,
    sort = sortConfig,
    filters = advancedFilters,
  ) => {
    setLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10
      const queryParams: any = { page, limit: effectiveLimit }
      if (search.trim()) queryParams.search = search
      if (status) queryParams.status = status
      if (association) queryParams.association_type = association
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (key.endsWith("_label")) return
        if (value !== undefined && value !== null && value !== "") {
          queryParams[key] = value
        }
      })
      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }

      const response = await api.get("/training/requests/list", { params: queryParams })
      if (response.data.success) {
        setRequests(response.data.data.training_requests || [])
        setPagination(response.data.data.pagination || pagination)
        const fetchedRequests: TrainingRequest[] = response.data.data.training_requests || []
        if (fetchedRequests.length) {
          setRequestOptions((prev) => {
            const optionMap = new Map<string, SelectOption>()
            prev.forEach((option) => optionMap.set(String(option.value), option))
            fetchedRequests.forEach((req) => {
              if (req.request_id) {
                optionMap.set(req.request_id, {
                  id: req.id,
                  label: req.request_id,
                  value: req.request_id,
                })
              }
            })
            return Array.from(optionMap.values())
          })
        }
      }
    } catch (error) {
      console.error("Error fetching training requests:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDropdowns = async () => {
    try {
      const [typesRes, institutesRes, designationsRes, projectsRes] = await Promise.all([
        api.get("/training/courses/active"),
        api.get("/training/institutes/active"),
        api.get("/designations/list", { params: { limit: 100, status: "active" } }),
        api.get("/projects/list", { params: { limit: 100, status: "ACTIVE" } }),
      ])
      if (typesRes.data.success) setTrainingTypes(typesRes.data.data.training_types || [])
      if (institutesRes.data.success) setInstitutes(institutesRes.data.data.institutes || [])
      if (designationsRes.data.success) {
        const designationOpts = (designationsRes.data.data.designations || []).map((designation: any) => ({
          id: designation.id,
          value: designation.id,
          label: designation.name,
        }))
        setDesignationOptions(designationOpts)
      }
      if (projectsRes.data.success) {
        const projectOpts = (projectsRes.data.data.projects || []).map((project: any) => ({
          id: project.id,
          value: project.id,
          label: project.project_name || `Project #${project.id}`,
        }))
        setProjectOptions(projectOpts)
      }
    } catch (error) {
      console.error("Error fetching dropdowns:", error)
    }
  }

  const createRequest = async (data: any) => {
    setActionLoading(true)
    const requestorDocs: File[] = data._requestor_docs || []
    const { _requestor_docs, ...submitData } = data
    try {
      const response = await api.post("/training/requests/create", submitData)
      if (response.data.success) {
        const requestId = response.data.data?.training_request?.id
        if (requestId && requestorDocs.length > 0) {
          try {
            const uploadedUrls = await uploadFilesWithPresignedUrls(requestorDocs)
            await Promise.all(
              uploadedUrls.map((url: string) =>
                api.post(`/training/requests/${requestId}/documents`, { document_url: url, document_type: "requestor_doc" })
              )
            )
          } catch (uploadErr) {
            console.error("Error uploading requestor docs:", uploadErr)
          }
        }
        await fetchRequests(searchTerm, statusFilter, associationFilter, 1, undefined, sortConfig, advancedFilters)
        setShowAdd(false)
        setDuplicateRequest(null)
      }
    } catch (error) {
      console.error("Error creating request:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateRequest = async (data: any) => {
    setActionLoading(true)
    const requestorDocs: File[] = data._requestor_docs || []
    const { _requestor_docs, ...submitData } = data
    try {
      const response = await api.put(`/training/requests/${submitData.id}`, submitData)
      if (response.data.success) {
        const requestId = submitData.id
        if (requestId && requestorDocs.length > 0) {
          try {
            const uploadedUrls = await uploadFilesWithPresignedUrls(requestorDocs)
            await Promise.all(
              uploadedUrls.map((url: string) =>
                api.post(`/training/requests/${requestId}/documents`, { document_url: url, document_type: "requestor_doc" })
              )
            )
          } catch (uploadErr) {
            console.error("Error uploading requestor docs:", uploadErr)
          }
        }
        await fetchRequests(searchTerm, statusFilter, associationFilter, pagination.page, undefined, sortConfig, advancedFilters)
        setShowEdit(false)
        setSelectedRequest(null)
      }
    } catch (error) {
      console.error("Error updating request:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const hrVerify = async (id: number, data: any) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/training/requests/${id}/hr-verify`, data)
      if (response.data.success) {
        await fetchRequests(searchTerm, statusFilter, associationFilter, pagination.page, undefined, sortConfig, advancedFilters)
        setShowDetail(false)
        setSelectedRequest(null)
      }
    } catch (error) {
      console.error("Error verifying request:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const scheduleRequest = async (id: number, data: any) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/training/requests/${id}/schedule`, data)
      if (response.data.success) {
        await fetchRequests(searchTerm, statusFilter, associationFilter, pagination.page)
        setShowDetail(false)
        setSelectedRequest(null)
      }
    } catch (error) {
      console.error("Error scheduling request:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const [cancelModal, setCancelModal] = useState<{ isOpen: boolean; request: TrainingRequest | null; reason: string }>({
    isOpen: false,
    request: null,
    reason: "",
  })

  const openCancelModal = (request: TrainingRequest) => {
    setCancelModal({ isOpen: true, request, reason: "" })
  }

  const closeCancelModal = () => {
    setCancelModal({ isOpen: false, request: null, reason: "" })
  }

  const cancelRequest = async () => {
    if (!cancelModal.request) return
    setActionLoading(true)
    try {
      const response = await api.delete(`/training/requests/${cancelModal.request.id}`, {
        data: {
          reason: cancelModal.reason || undefined,
        },
      })
      if (response.data.success) {
        await fetchRequests(searchTerm, statusFilter, associationFilter, pagination.page, undefined, sortConfig, advancedFilters)
        closeCancelModal()
      }
    } catch (error) {
      console.error("Error cancelling request:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchRequests(debouncedSearch, statusFilter, associationFilter, 1, undefined, sortConfig, advancedFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchRequests(searchTerm, status, associationFilter, 1, undefined, sortConfig, advancedFilters)
  }
  const handleAssociationFilter = (association: string) => {
    setAssociationFilter(association)
    fetchRequests(searchTerm, statusFilter, association, 1, undefined, sortConfig, advancedFilters)
  }
  const handlePageChange = (page: number) => {
    fetchRequests(searchTerm, statusFilter, associationFilter, page, undefined, sortConfig, advancedFilters)
  }
  const handleLimitChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }))
    fetchRequests(searchTerm, statusFilter, associationFilter, 1, limit, sortConfig, advancedFilters)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchRequests(searchTerm, statusFilter, associationFilter, pagination.page, undefined, sort, advancedFilters)
  }

  const handleAdvancedFilter = (filters: RequestFilters) => {
    setAdvancedFilters(filters)
    fetchRequests(searchTerm, statusFilter, associationFilter, 1, undefined, sortConfig, filters)
  }

  const handleEdit = async (request: TrainingRequest) => {
    setActionLoading(true)
    try {
      const response = await api.get(`/training/requests/${request.id}`)
      if (response.data.success) {
        const detailedRequest: TrainingRequest = response.data.data.training_request || request
        setSelectedRequest(detailedRequest)
        setShowEdit(true)
        return
      }
      setSelectedRequest(request)
      setShowEdit(true)
    } catch (error) {
      console.error("Error fetching request details:", error)
      setSelectedRequest(request)
      setShowEdit(true)
    } finally {
      setActionLoading(false)
    }
  }
  const handleDuplicate = async (request: TrainingRequest) => {
    setActionLoading(true)
    try {
      const response = await api.get(`/training/requests/${request.id}`)
      const detailedRequest: TrainingRequest = response.data.success
        ? response.data.data.training_request || request
        : request
      setDuplicateRequest(detailedRequest)
      setShowAdd(true)
    } catch (error) {
      console.error("Error fetching request for duplicate:", error)
      setDuplicateRequest(request)
      setShowAdd(true)
    } finally {
      setActionLoading(false)
    }
  }
  const handleViewDetail = (request: TrainingRequest) => { navigate(`/training/requests/${request.id}`) }
  const handleCloseModals = () => { setShowAdd(false); setShowEdit(false); setShowDetail(false); setSelectedRequest(null); setDuplicateRequest(null) }

  useEffect(() => { fetchRequests(); fetchDropdowns() }, [])

  useEffect(() => {
    const state = location.state as { duplicateRequest?: TrainingRequest } | null
    if (state?.duplicateRequest) {
      setDuplicateRequest(state.duplicateRequest)
      setShowAdd(true)
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  const trainingTypeOptions = useMemo<SelectOption[]>(() => (
    trainingTypes.map((type) => ({ id: type.id, value: type.id, label: type.training_name }))
  ), [trainingTypes])

  const trainingInstituteOptions = useMemo<SelectOption[]>(() => (
    institutes.map((institute) => ({ id: institute.id, value: institute.id, label: institute.institute_name }))
  ), [institutes])

  return (
    <div>
      <RequestsList
        requests={requests}
        pagination={pagination}
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onAssociationFilter={handleAssociationFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAdd={() => setShowAdd(true)}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onView={handleViewDetail}
        onCancel={openCancelModal}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
        onAdvancedFilter={handleAdvancedFilter}
        advancedFilters={advancedFilters}
        trainingTypeOptions={trainingTypeOptions}
        designationOptions={designationOptions}
        projectOptions={projectOptions}
        trainingInstituteOptions={trainingInstituteOptions}
        requestOptions={requestOptions}
      />
      <AddRequestOffcanvas
        isOpen={showAdd}
        onClose={handleCloseModals}
        onSubmit={createRequest}
        onBulkImportComplete={() => fetchRequests(searchTerm, statusFilter, associationFilter, 1, undefined, sortConfig, advancedFilters)}
        trainingTypes={trainingTypes}
        loading={actionLoading}
        duplicateFrom={duplicateRequest}
      />
      <EditRequestOffcanvas isOpen={showEdit} onClose={handleCloseModals} onSubmit={updateRequest} request={selectedRequest} trainingTypes={trainingTypes} loading={actionLoading} />
      <RequestDetailOffcanvas
        isOpen={showDetail} onClose={handleCloseModals} request={selectedRequest}
        onHrVerify={hrVerify} onSchedule={scheduleRequest} institutes={institutes} loading={actionLoading}
      />
      {cancelModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => !actionLoading && closeCancelModal()} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-2xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Cancel Request</h3>
                  <p className="text-sm text-gray-500 mt-1">Request ID: {cancelModal.request?.request_id}</p>
                </div>
                <button
                  type="button"
                  onClick={closeCancelModal}
                  className="p-2 rounded hover:bg-gray-100 text-gray-500"
                  disabled={actionLoading}
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for cancelling this request</label>
                <textarea
                  value={cancelModal.reason}
                  onChange={(e) => setCancelModal((prev) => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="Enter cancellation reason"
                  disabled={actionLoading}
                />
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  <p className="font-medium">Note: This action will permanently cancel the request. This action may not be reversible.</p>
                </div>
                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={closeCancelModal}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                    disabled={actionLoading}
                  >
                    Keep Request
                  </button>
                  <button
                    type="button"
                    onClick={cancelRequest}
                    disabled={!cancelModal.reason.trim() || actionLoading}
                    className="px-4 py-2 rounded text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 font-medium"
                  >
                    Cancel Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
